import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { grantService } from '../../../utils/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';

const AddGrantModal = ({ onClose, onGrantAdded, focusAreas }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();

  const watchedFocusAreas = watch('focus_areas', []);
  const watchedEligibilityCriteria = watch('eligibility_criteria', []);
  const watchedRequirements = watch('requirements', []);

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'closing_soon', label: 'Closing Soon' },
    { value: 'closed', label: 'Closed' },
    { value: 'review', label: 'Under Review' },
    { value: 'awarded', label: 'Awarded' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
    { value: 'CAD', label: 'CAD' }
  ];

  const commonEligibilityCriteria = [
    'Early-stage startups',
    'University students',
    'Graduate students',
    'Faculty members',
    'City government employees',
    'Non-profit organizations',
    'Small businesses (< 50 employees)',
    'Medium businesses (50-250 employees)',
    'Large enterprises',
    'International applicants welcome',
    'US/Canada residents only',
    'Must be 18+ years old'
  ];

  const commonRequirements = [
    'Detailed project proposal',
    'Budget breakdown',
    'Timeline and milestones',
    'Team member profiles',
    'Letters of recommendation',
    'Proof of concept or prototype',
    'Market research analysis',
    'Impact assessment',
    'Technical specifications',
    'Financial statements',
    'Business plan',
    'Partnership agreements'
  ];

  const handleFocusAreaChange = (area, checked) => {
    const current = watchedFocusAreas || [];
    if (checked) {
      setValue('focus_areas', [...current, area]);
    } else {
      setValue('focus_areas', current?.filter(item => item !== area));
    }
  };

  const handleEligibilityChange = (criteria, checked) => {
    const current = watchedEligibilityCriteria || [];
    if (checked) {
      setValue('eligibility_criteria', [...current, criteria]);
    } else {
      setValue('eligibility_criteria', current?.filter(item => item !== criteria));
    }
  };

  const handleRequirementChange = (requirement, checked) => {
    const current = watchedRequirements || [];
    if (checked) {
      setValue('requirements', [...current, requirement]);
    } else {
      setValue('requirements', current?.filter(item => item !== requirement));
    }
  };

  const onSubmit = async (data) => {
    if (!user) return;

    setSubmitting(true);
    setError(null);

    try {
      const grantData = {
        ...data,
        created_by: user?.id,
        amount_min: data?.amount_min ? parseFloat(data?.amount_min) : null,
        amount_max: data?.amount_max ? parseFloat(data?.amount_max) : null,
        deadline: new Date(data.deadline)?.toISOString(),
        focus_areas: data?.focus_areas || [],
        eligibility_criteria: data?.eligibility_criteria || [],
        requirements: data?.requirements || [],
        view_count: 0
      };

      const { data: newGrant, error: createError } = await grantService?.createGrant(grantData);

      if (createError) {
        setError('Failed to create grant opportunity. Please try again.');
        return;
      }

      onGrantAdded(newGrant);
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Add New Grant Opportunity</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <p className="text-destructive text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Grant Title *</label>
              <Input
                {...register('title', { required: 'Title is required' })}
                placeholder="e.g., Urban Innovation Challenge 2025"
                error={errors?.title?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Provider Organization *</label>
              <Input
                {...register('provider_organization', { required: 'Organization is required' })}
                placeholder="e.g., Smart Cities Foundation"
                error={errors?.provider_organization?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <Select
                options={statusOptions}
                {...register('status')}
                defaultValue="open"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">Description *</label>
            <textarea
              {...register('description', { required: 'Description is required' })}
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Describe the grant opportunity, its purpose, and objectives..."
            />
            {errors?.description && (
              <p className="text-destructive text-sm mt-1">{errors?.description?.message}</p>
            )}
          </div>

          {/* Funding Information */}
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium mb-2">Minimum Amount</label>
              <Input
                type="number"
                {...register('amount_min')}
                placeholder="10000"
                min="0"
                step="1000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Maximum Amount *</label>
              <Input
                type="number"
                {...register('amount_max', { required: 'Maximum amount is required' })}
                placeholder="100000"
                min="0"
                step="1000"
                error={errors?.amount_max?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Currency</label>
              <Select
                options={currencyOptions}
                {...register('currency')}
                defaultValue="USD"
              />
            </div>
          </div>

          {/* Deadline */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium mb-2">Application Deadline *</label>
              <Input
                type="datetime-local"
                {...register('deadline', { required: 'Deadline is required' })}
                error={errors?.deadline?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Application URL</label>
              <Input
                type="url"
                {...register('application_url')}
                placeholder="https://example.com/apply"
              />
            </div>
          </div>

          {/* Focus Areas */}
          <div>
            <label className="block text-sm font-medium mb-2">Focus Areas</label>
            <div className="grid gap-2 md:grid-cols-3">
              {focusAreas?.map((area) => (
                <Checkbox
                  key={area}
                  checked={watchedFocusAreas?.includes(area)}
                  onChange={(checked) => handleFocusAreaChange(area, checked)}
                  label={area}
                />
              ))}
            </div>
          </div>

          {/* Eligibility Criteria */}
          <div>
            <label className="block text-sm font-medium mb-2">Eligibility Criteria</label>
            <div className="grid gap-2 md:grid-cols-2">
              {commonEligibilityCriteria?.map((criteria) => (
                <Checkbox
                  key={criteria}
                  checked={watchedEligibilityCriteria?.includes(criteria)}
                  onChange={(checked) => handleEligibilityChange(criteria, checked)}
                  label={criteria}
                />
              ))}
            </div>
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-sm font-medium mb-2">Application Requirements</label>
            <div className="grid gap-2 md:grid-cols-2">
              {commonRequirements?.map((requirement) => (
                <Checkbox
                  key={requirement}
                  checked={watchedRequirements?.includes(requirement)}
                  onChange={(checked) => handleRequirementChange(requirement, checked)}
                  label={requirement}
                />
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create Grant
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGrantModal;